import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { KnowledgeSourceSelectorComponent } from '../../shared/design-system/knowledge-source-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { GrowthAgentService, GrowthStrategyArtifact } from './growth-agent.service';
import { GrowthContentBrief, GrowthContentTemplatePack, GrowthMarketingService } from './growth-marketing.service';
import { buildGrowthMarketingChannelOptions } from './growth-channel.constants';
import { strategyArtifactTypeI18nKey } from './growth-status.util';

interface BriefChannelTargetRow {
  channelKey: string;
  priority: number;
}

interface BriefSourceBindingRow {
  bindingType: string;
  bindingRef: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-ace-brief-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    GrowthSegmentSelectorComponent,
    GrowthContentAssetSelectorComponent,
    KnowledgeSourceSelectorComponent,
    OfferingSelectorComponent,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="eyebrowLabel()" [title]="title()" [description]="descriptionLabel()">
      <div class="toolbar">
        <a pButton routerLink="/growth/marketing/ace-studio" severity="secondary" [label]="backLabel()"></a>
      </div>
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.content.ace.dialogs.brief') }}</h3>
            <ain-info-hint [titleKey]="'growth.content.ace.help.sourceBindings.title'" [descriptionKey]="'growth.content.ace.help.sourceBindings.description'" />
          </div>
          <div class="grid">
            <ain-form-field [label]="t('growth.content.ace.fields.ownerScope')">
              <p-select [options]="ownerScopeOptions" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.ownerScope" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.contentType')">
              <p-select [options]="contentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.contentType" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.targetAudience')"><input pInputText [(ngModel)]="briefModel.targetAudience" /></ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.goal')"><input pInputText [(ngModel)]="briefModel.goal" /></ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.locale')"><input pInputText [(ngModel)]="briefModel.locale" /></ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.strategyArtifact')">
              <p-select
                [options]="strategyArtifactOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="briefModel.strategyArtifactId"
                appendTo="body"
                [showClear]="false" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.content.ace.fields.templatePack')">
              <p-select [options]="templatePackOptions()" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.templatePackKey" appendTo="body" [showClear]="true" />
            </ain-form-field>
          </div>

          <div class="sub-title">{{ t('growth.content.ace.sections.channelTargets') }}</div>
          @for (row of channelTargets; track $index) {
            <div class="row-grid">
              <ain-form-field [label]="t('growth.content.ace.fields.channel')">
                <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.content.ace.fields.priority')"><p-inputNumber [(ngModel)]="row.priority" [min]="1" /></ain-form-field>
              <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="channelTargets.splice($index, 1)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="channelTargets.push({ channelKey: 'web-blog', priority: 1 })"></button>

          <div class="sub-title">{{ t('growth.content.ace.sections.sourceBindings') }}</div>
          @for (row of sourceBindings; track $index) {
            <div class="row-grid">
              <ain-form-field [label]="t('growth.content.ace.fields.bindingType')">
                <p-select [options]="bindingTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.bindingType" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.content.ace.fields.bindingRef')">
                @if (row.bindingType === 'offering' || row.bindingType === 'catalog') {
                  <ain-offering-selector [(ngModel)]="row.bindingRef" />
                } @else if (row.bindingType === 'segment') {
                  <ain-growth-segment-selector [(ngModel)]="row.bindingRef" />
                } @else if (row.bindingType === 'content-asset') {
                  <ain-growth-content-asset-selector [(ngModel)]="row.bindingRef" />
                } @else if (row.bindingType === 'knowledge') {
                  <ain-knowledge-source-selector [(ngModel)]="row.bindingRef" />
                }
              </ain-form-field>
              <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="sourceBindings.splice($index, 1)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="sourceBindings.push({ bindingType: 'catalog', bindingRef: '' })"></button>

          <div class="actions">
            <button pButton type="button" [label]="isNew() ? createLabel() : saveLabel()" [disabled]="!canSave()" (click)="save()"></button>
            @if (!isNew() && brief()) {
              <button pButton type="button" [label]="t('growth.content.ace.actions.produce')" (click)="produce(brief()!)"></button>
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .sub-title { font-weight: 600; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthAceBriefDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(GrowthMarketingService);
  private readonly agentService = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(false);
  readonly brief = signal<GrowthContentBrief | null>(null);
  readonly templatePacks = signal<GrowthContentTemplatePack[]>([]);
  readonly strategyArtifacts = signal<GrowthStrategyArtifact[]>([]);

  briefModel = {
    ownerScope: 'tenant',
    contentType: 'article',
    targetAudience: '',
    goal: '',
    locale: 'fa-IR',
    strategyArtifactId: null as string | null,
    templatePackKey: ''
  };
  channelTargets: BriefChannelTargetRow[] = [{ channelKey: 'web-blog', priority: 1 }];
  sourceBindings: BriefSourceBindingRow[] = [{ bindingType: 'catalog', bindingRef: '' }];

  readonly ownerScopeOptions = [{ value: 'tenant', label: 'Tenant' }, { value: 'platform', label: 'Platform' }];
  readonly contentTypeOptions = [
    { value: 'article', label: 'Article' },
    { value: 'landing-page', label: 'Landing Page' },
    { value: 'social-post', label: 'Social post' },
    { value: 'brochure', label: 'Brochure' },
    { value: 'pitch-deck', label: 'Pitch Deck' },
    { value: 'product-catalog', label: 'Product Catalog' },
    { value: 'company-profile', label: 'Company Profile' },
    { value: 'video-script', label: 'Video Script' },
    { value: 'demo-storyboard', label: 'Demo Storyboard' }
  ];
  readonly bindingTypeOptions = [
    { value: 'catalog', label: 'Catalog / Offering' },
    { value: 'offering', label: 'Offering' },
    { value: 'segment', label: 'Segment' },
    { value: 'content-asset', label: 'Content asset' },
    { value: 'knowledge', label: 'Knowledge corpus' }
  ];
  readonly channelOptions = buildGrowthMarketingChannelOptions(key => this.t(key));

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('briefId');
    this.isNew.set(!id || id === 'new');
    this.service.listContentTemplatePacks().subscribe(items => this.templatePacks.set(items));
    this.agentService.listStrategyArtifacts().subscribe(items => this.strategyArtifacts.set(items));
    if (this.isNew()) {
      this.loading.set(false);
      return;
    }
    this.service.getContentBrief(id!).subscribe({
      next: brief => {
        this.bindBrief(brief);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.error'));
        this.loading.set(false);
      }
    });
  }

  title(): string {
    if (this.isNew()) {
      return this.tr('growth.content.ace.actions.newBrief', 'بریف جدید');
    }
    return this.tr('common.edit', 'ویرایش بریف');
  }

  eyebrowLabel(): string {
    return this.tr('growth.eyebrow', 'رشد');
  }

  descriptionLabel(): string {
    return this.tr(
      'growth.content.ace.studio.description',
      'بریف محتوا را مدیریت کنید، اتصال‌ها را تنظیم کنید و تولید را از مسیر استاندارد ادامه دهید.'
    );
  }

  backLabel(): string {
    return this.tr('common.back', 'بازگشت');
  }

  createLabel(): string {
    return this.tr('common.create', 'ایجاد');
  }

  saveLabel(): string {
    return this.tr('common.save', 'ذخیره');
  }

  templatePackOptions() {
    return this.templatePacks()
      .filter(item => item.contentType === this.briefModel.contentType && item.ownerScope === this.briefModel.ownerScope && item.isActive && item.isCertified)
      .map(item => ({ value: item.key, label: `${item.key} (${item.version})` }));
  }

  strategyArtifactOptions(): Array<{ value: string; label: string }> {
    return this.strategyArtifacts()
      .map(item => ({
        value: item.id,
        label: `${item.title} (${this.t(strategyArtifactTypeI18nKey(item.artifactType))})`
      }));
  }

  canSave(): boolean {
    return this.briefModel.targetAudience.trim().length > 0
      && this.briefModel.goal.trim().length > 0
      && !!this.briefModel.strategyArtifactId
      && this.channelTargets.length > 0
      && this.sourceBindings.length > 0;
  }

  save(): void {
    const payload = {
      id: this.brief()?.id ?? null,
      ownerScope: this.briefModel.ownerScope,
      contentType: this.briefModel.contentType,
      targetAudience: this.briefModel.targetAudience,
      goal: this.briefModel.goal,
      locale: this.briefModel.locale,
      channelTargetsJson: JSON.stringify(this.channelTargets),
      sourceBindingsJson: JSON.stringify(this.sourceBindings),
      strategyArtifactId: this.briefModel.strategyArtifactId,
      correlationId: null,
      idempotencyKey: this.brief() ? `brief-update-${this.brief()!.id}` : `brief-create-${Date.now()}`
    };
    const request$ = this.brief()
      ? this.service.updateContentBrief(this.brief()!.id, payload)
      : this.service.createContentBrief(payload);
    request$.subscribe(saved => {
      this.bindBrief(saved);
      if (this.isNew()) {
        this.router.navigate(['/growth/marketing/ace-studio/briefs', saved.id]);
      }
    });
  }

  produce(brief: GrowthContentBrief): void {
    const selectedTemplate = this.templatePacks()
      .find(item => item.contentType === brief.contentType && item.ownerScope === brief.ownerScope && item.isActive && item.isCertified);
    if (!selectedTemplate) {
      return;
    }
    this.service.produceContentBrief(brief.id, {
      templatePackKey: selectedTemplate.key,
      idempotencyKey: `produce-${brief.id}-${Date.now()}`
    }).subscribe({
      next: result => {
        this.router.navigate(['/growth/marketing/article-publishing', result.output.id], {
          queryParams: { briefId: brief.id }
        });
      }
    });
  }

  private bindBrief(brief: GrowthContentBrief): void {
    this.isNew.set(false);
    this.brief.set(brief);
    this.briefModel = {
      ownerScope: brief.ownerScope,
      contentType: brief.contentType,
      targetAudience: brief.targetAudience,
      goal: brief.goal,
      locale: brief.locale,
      strategyArtifactId: brief.strategyArtifactId ?? null,
      templatePackKey: ''
    };
    this.channelTargets = this.normalizeChannelTargets(this.parseJsonUnknown(brief.channelTargetsJson));
    this.sourceBindings = this.normalizeSourceBindings(this.parseJsonUnknown(brief.sourceBindingsJson));
  }

  private parseJsonUnknown(json: string): unknown {
    try {
      return JSON.parse(json) as unknown;
    } catch {
      return null;
    }
  }

  private normalizeChannelTargets(value: unknown): BriefChannelTargetRow[] {
    if (!Array.isArray(value)) {
      return [{ channelKey: 'web-blog', priority: 1 }];
    }

    const normalized = value
      .map(item => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const row = item as Record<string, unknown>;
        const channelKey = typeof row['channelKey'] === 'string' && row['channelKey'].trim().length > 0
          ? row['channelKey'].trim()
          : null;
        if (!channelKey) {
          return null;
        }
        const priorityRaw = typeof row['priority'] === 'number' ? row['priority'] : Number(row['priority']);
        const priority = Number.isFinite(priorityRaw) && priorityRaw > 0 ? Math.trunc(priorityRaw) : 1;
        return { channelKey, priority };
      })
      .filter((item): item is BriefChannelTargetRow => item !== null);

    return normalized.length > 0 ? normalized : [{ channelKey: 'web-blog', priority: 1 }];
  }

  private normalizeSourceBindings(value: unknown): BriefSourceBindingRow[] {
    if (!Array.isArray(value)) {
      return [{ bindingType: 'catalog', bindingRef: '' }];
    }

    const normalized = value
      .map(item => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const row = item as Record<string, unknown>;
        const bindingType = typeof row['bindingType'] === 'string' && row['bindingType'].trim().length > 0
          ? row['bindingType'].trim()
          : null;
        if (!bindingType) {
          return null;
        }
        const bindingRef = typeof row['bindingRef'] === 'string' ? row['bindingRef'] : '';
        return { bindingType, bindingRef };
      })
      .filter((item): item is BriefSourceBindingRow => item !== null);

    return normalized.length > 0 ? normalized : [{ bindingType: 'catalog', bindingRef: '' }];
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private tr(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }
}
